import BalanceIcon from '@mui/icons-material/Balance';
import LanguageIcon from '@mui/icons-material/Language';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Slider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import Adsense from '../Ads/Adsense';
import { logEvent } from '../../utils/ga';
import styles from './GuidePage.module.css';

const SECTION_ITEMS = [
  { id: 'diversification', label: '분산투자' },
  { id: 'age-allocation', label: '나이별 자산배분' },
  { id: 'etf', label: 'ETF 입문' },
  { id: 'safe-assets', label: '예금·채권' },
] as const;

const ETF_CARDS = [
  { ticker: 'KODEX 200', name: '국내 주식 ETF', region: '국내', stats: ['10년 수익률: ~80%', '연 보수: 0.15%'] },
  { ticker: 'TIGER S&P500', name: '미국 주식 ETF', region: '미국', stats: ['10년 수익률: ~250%', '연 보수: 0.07%'] },
  { ticker: 'TIGER MSCI', name: '선진국 분산 ETF', region: '글로벌', stats: ['23개국 1,500개 기업', '연 보수: 0.25%'] },
  { ticker: 'KODEX 국채', name: '채권 ETF', region: '채권', stats: ['원금 변동 낮음', '연 보수: 0.05%'] },
] as const;

const ALLOCATION_COLORS = {
  stock: '#3b82f6',
  bond: '#10b981',
  cash: '#f59e0b',
} as const;

function getAllocation(age: number) {
  const bands = [
    { max: 34, stock: 80, bond: 15, cash: 5 },
    { max: 44, stock: 70, bond: 20, cash: 10 },
    { max: 54, stock: 55, bond: 30, cash: 15 },
    { max: 64, stock: 35, bond: 45, cash: 20 },
    { max: 99, stock: 20, bond: 50, cash: 30 },
  ];

  return bands.find((band) => age <= band.max) ?? bands[bands.length - 1];
}

const CONCEPT_CARDS = [
  {
    Icon: BalanceIcon,
    title: '상관관계 분산',
    body: '같이 오르내리지 않는 자산을 섞으면 큰 손실 구간의 충격을 줄일 수 있습니다.',
  },
  {
    Icon: TrendingDownIcon,
    title: '하락 방어',
    body: '주식이 약세일 때 채권과 현금이 완충 역할을 하며 회복 시간을 벌어줍니다.',
  },
  {
    Icon: LanguageIcon,
    title: '지역 분산',
    body: '한국 한 시장에 집중하지 않고 미국과 선진국까지 넓히면 구조적 리스크를 낮춥니다.',
  },
] as const;

const SAFE_ASSET_ITEMS = [
  {
    title: '예금 기초',
    body: '예금자 보호는 금융회사별 1인당 5천만원 한도입니다. 만기를 나눠두는 사다리 전략을 쓰면 금리 변동에 대응하면서 현금흐름도 확보할 수 있습니다.',
  },
  {
    title: '채권 ETF',
    body: '국채 ETF는 방어력이 높고, 우량 회사채 ETF는 약간 높은 수익을 기대할 수 있습니다. 포트폴리오에서는 주식 하락 시 변동성을 낮추는 역할이 핵심입니다.',
  },
  {
    title: '인플레이션 헤지',
    body: '물가연동채와 배당주는 현금성 자산만 들고 있을 때 생기는 실질 구매력 하락을 보완하는 데 유용합니다.',
  },
  {
    title: '비상금 원칙',
    body: '최소 6개월 생활비는 즉시 꺼낼 수 있는 현금성 자산으로 유지해야 합니다. 투자 계좌를 비상금 통장처럼 쓰면 장기 전략이 무너집니다.',
  },
] as const;

export default function GuidePage() {
  const [activeSection, setActiveSection] =
    useState<(typeof SECTION_ITEMS)[number]['id']>('diversification');
  const [age, setAge] = useState(35);
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntry = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

        if (visibleEntry?.target.id) {
          setActiveSection(visibleEntry.target.id as (typeof SECTION_ITEMS)[number]['id']);
        }
      },
      {
        rootMargin: '-88px 0px -55% 0px',
        threshold: [0.2, 0.4, 0.6],
      },
    );

    SECTION_ITEMS.forEach(({ id }) => {
      const element = sectionRefs.current[id];
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, []);

  const allocation = useMemo(() => getAllocation(age), [age]);
  const allocationData = useMemo(
    () => [
      { name: '주식', value: allocation.stock, color: ALLOCATION_COLORS.stock },
      { name: '채권', value: allocation.bond, color: ALLOCATION_COLORS.bond },
      { name: '현금', value: allocation.cash, color: ALLOCATION_COLORS.cash },
    ],
    [allocation],
  );

  const scrollToSection = (sectionId: (typeof SECTION_ITEMS)[number]['id']) => {
    const element = sectionRefs.current[sectionId];
    if (!element) {
      return;
    }

    logEvent('engagement', 'guide_sidebar_section_click', sectionId);
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className={styles.root}>
      <Helmet>
        <title>투자 가이드 — 분산투자·ETF·자산배분 전략 | 노후든든</title>
        <meta
          name="description"
          content="계산기 숫자 뒤에 있는 원리를 이해하면 결과가 달라집니다. 분산투자, 나이별 자산배분, ETF 입문, 예금·채권 활용법을 전문가 관점으로 설명합니다."
        />
      </Helmet>

      <header className={styles.pageHeader}>
        <div className={styles.pageHeaderInner}>
          <p className={styles.pageLabel}>투자 가이드</p>
          <h1 className={styles.pageTitle}>계산기 숫자 뒤의 원리를 이해하면 결과가 달라집니다</h1>
          <p className={styles.pageIntro}>
            분산투자, 나이별 자산배분, ETF, 안전자산의 역할까지 한 번에 정리했습니다.
            도구로 계산하고, 가이드로 판단 기준까지 함께 가져가세요.
          </p>
        </div>
      </header>

      <div className={styles.guideLayout}>
        <aside className={styles.sidebar}>
          <p className={styles.sidebarTitle}>Contents</p>
          {SECTION_ITEMS.map((section) => (
            <button
              key={section.id}
              type="button"
              className={`${styles.sidebarItem} ${activeSection === section.id ? styles.sidebarItemActive : ''}`}
              onClick={() => scrollToSection(section.id)}
            >
              {section.label}
            </button>
          ))}
        </aside>

        <main className={styles.content}>
          <section
            id="diversification"
            ref={(node) => {
              sectionRefs.current.diversification = node;
            }}
            className={styles.section}
          >
            <h2 className={styles.sectionHeading}>분산투자 — 모르면 손해, 알면 당연한 원칙</h2>
            <div className={styles.insightBox}>
              <p className={styles.insightBoxTitle}>Key Insight</p>
              <p className={styles.insightBoxBody}>
                상관관계 0인 두 자산을 50:50으로 섞으면 변동성은 평균보다 낮아집니다
              </p>
            </div>
            <p className={styles.sectionBody}>
              분산투자는 수익률을 포기하는 전략이 아니라, 같은 기대수익을 더 낮은 위험으로 얻기 위한 기본 원칙입니다.
              한 자산이 흔들릴 때 다른 자산이 완충해주면 손실 폭이 줄고, 결국 오래 버티는 힘이 생깁니다.
              노후 준비에서는 높은 수익보다 중도 이탈하지 않는 구조가 더 중요합니다.
            </p>
            <div className={styles.conceptCards}>
              {CONCEPT_CARDS.map(({ Icon, title, body }) => (
                <article key={title} className={styles.conceptCard}>
                  <div className={styles.conceptCardIcon}>
                    <Icon fontSize="inherit" />
                  </div>
                  <h3 className={styles.conceptCardTitle}>{title}</h3>
                  <p className={styles.conceptCardBody}>{body}</p>
                </article>
              ))}
            </div>
            <Link
              to="/compound"
              className={styles.sectionCTA}
              onClick={() => logEvent('engagement', 'guide_section_cta_click', '/compound')}
            >
              복리 시뮬레이터로 확인하기
            </Link>
          </section>

          <section
            id="age-allocation"
            ref={(node) => {
              sectionRefs.current['age-allocation'] = node;
            }}
            className={styles.section}
          >
            <h2 className={styles.sectionHeading}>나이별 자산배분 — 100에서 나이를 뺀 만큼만 주식에</h2>
            <p className={styles.sectionBody}>
              젊을수록 회복 시간이 길기 때문에 성장 자산 비중을 높게 가져갈 수 있고,
              은퇴가 가까울수록 채권과 현금 비중을 늘려 큰 하락을 피하는 편이 유리합니다.
              아래 슬라이더로 나이를 움직여 현재 권장 배분 예시를 확인해보세요.
            </p>

            <div className={styles.sliderSection}>
              <div className={styles.sliderLabel}>
                <span className={styles.sliderLabelText}>현재 나이</span>
                <span className={styles.sliderAgeValue}>{age}세</span>
              </div>
              <Slider
                value={age}
                min={25}
                max={65}
                step={1}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => `${value}세`}
                onChange={(_, value) => {
                  logEvent('engagement', 'guide_allocation_age_change', `${value}`);
                  setAge(value as number);
                }}
                sx={{
                  color: '#000033',
                  '& .MuiSlider-thumb': {
                    width: 18,
                    height: 18,
                  },
                }}
              />
            </div>

            <div className={styles.allocationChartRow}>
              <div className={styles.allocationPieWrapper}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={allocationData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={60}
                      outerRadius={110}
                      paddingAngle={2}
                      animationDuration={400}
                    >
                      {allocationData.map((entry) => (
                        <Cell key={entry.name} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={((value: number) => [`${value}%`, '비중']) as any} />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className={styles.allocationLegend}>
                {allocationData.map((item) => (
                  <div key={item.name} className={styles.allocationLegendItem}>
                    <span className={styles.allocationDot} style={{ background: item.color }} />
                    <span className={styles.allocationLegendLabel}>{item.name}</span>
                    <span className={styles.allocationLegendPct}>{item.value}%</span>
                  </div>
                ))}
              </div>
            </div>

            <table className={styles.allocationTable}>
              <thead>
                <tr>
                  <th>나이대</th>
                  <th>주식</th>
                  <th>채권·예금</th>
                  <th>핵심 전략</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>20~30대</td>
                  <td>70~90%</td>
                  <td>10~30%</td>
                  <td>성장 극대화</td>
                </tr>
                <tr>
                  <td>40대</td>
                  <td>60~70%</td>
                  <td>30~40%</td>
                  <td>성장 유지</td>
                </tr>
                <tr>
                  <td>50대</td>
                  <td>40~60%</td>
                  <td>40~60%</td>
                  <td>균형 전환</td>
                </tr>
                <tr>
                  <td>60대 이후</td>
                  <td>20~40%</td>
                  <td>60~80%</td>
                  <td>원금 보전 우선</td>
                </tr>
              </tbody>
            </table>
          </section>

          <section
            id="etf"
            ref={(node) => {
              sectionRefs.current.etf = node;
            }}
            className={styles.section}
          >
            <h2 className={styles.sectionHeading}>ETF — 전문가 없이도 전문가 수준의 분산투자</h2>
            <div className={styles.insightBox}>
              <p className={styles.insightBoxTitle}>Key Insight</p>
              <p className={styles.insightBoxBody}>
                액티브 펀드 80~90%는 10년 장기로 인덱스 ETF를 이기지 못합니다
              </p>
            </div>
            <p className={styles.sectionBody}>
              ETF는 여러 종목을 한 번에 담아 분산투자를 쉽게 만들어주는 도구입니다.
              수수료가 낮고 운용 방식이 투명해 장기 투자자에게 특히 유리합니다.
              복잡한 종목 선정 대신, 좋은 시장을 싸게 오래 보유하는 데 집중할 수 있습니다.
            </p>
            <div className={styles.etfCards}>
              {ETF_CARDS.map((card) => (
                <article key={card.ticker} className={styles.etfCard}>
                  <div className={styles.etfCardHeader}>
                    <h3 className={styles.etfCardTicker}>{card.ticker}</h3>
                    <p className={styles.etfCardName}>{card.name}</p>
                  </div>
                  <div className={styles.etfCardBody}>
                    <span className={styles.etfCardRegion}>{card.region}</span>
                    <ul className={styles.etfCardStats}>
                      {card.stats.map((stat) => (
                        <li key={stat}>{stat}</li>
                      ))}
                    </ul>
                  </div>
                </article>
              ))}
            </div>
            <Adsense adClient="ca-pub-9359050149154028" adSlot="6981793359" />
          </section>

          <section
            id="safe-assets"
            ref={(node) => {
              sectionRefs.current['safe-assets'] = node;
            }}
            className={styles.section}
          >
            <h2 className={styles.sectionHeading}>예금·채권 — 포트폴리오의 방패이자 재원</h2>
            <div className={styles.insightBox}>
              <p className={styles.insightBoxTitle}>Key Insight</p>
              <p className={styles.insightBoxBody}>
                2008년 금융위기 — 주식60+채권40 포트폴리오 손실 -25% vs 주식100% 손실 -50%
              </p>
            </div>
            <p className={styles.sectionBody}>
              안전자산은 수익을 높이기 위한 장치가 아니라, 위기 때 포트폴리오를 지키고
              다시 투자할 재원을 남겨두기 위한 장치입니다. 예금과 채권을 적절히 섞어두면
              하락장에서 생활비와 리밸런싱 자금을 분리할 수 있습니다.
            </p>
            {SAFE_ASSET_ITEMS.map((item, index) => (
              <Accordion key={item.title} disableGutters defaultExpanded={index === 0}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  {item.title}
                </AccordionSummary>
                <AccordionDetails>{item.body}</AccordionDetails>
              </Accordion>
            ))}
            <Link
              to="/retirement"
              className={styles.sectionCTA}
              onClick={() => logEvent('engagement', 'guide_section_cta_click', '/retirement')}
            >
              노후 자금 계산기로 연결하기
            </Link>
          </section>
        </main>
      </div>
    </div>
  );
}
